import { Button, cn, Dialog, DialogContent, DialogTrigger } from '@estac1/ui';
import { useQuery } from '@tanstack/react-query';
import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import {
  BarChart3,
  Building2,
  Camera,
  ClipboardList,
  LayoutDashboard,
  Menu,
  MessagesSquare,
  Tag,
} from 'lucide-react';
import { useState } from 'react';
import { GlobalSearch } from '../components/global-search.js';
import { api } from '../lib/api.js';

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
}

const NAV: NavItem[] = [
  { to: '/', label: 'Visão geral', icon: LayoutDashboard },
  { to: '/clients', label: 'Clientes', icon: Building2 },
  { to: '/metrics', label: 'Métricas', icon: BarChart3 },
  { to: '/settings/pricing', label: 'Preços', icon: Tag },
  { to: '/alpr/review', label: 'ALPR', icon: Camera },
  { to: '/assistant', label: 'Assistente', icon: MessagesSquare },
  { to: '/audit', label: 'Auditoria', icon: ClipboardList },
];

const isActivePath = (pathname: string, to: string): boolean =>
  to === '/' ? pathname === '/' : pathname.startsWith(to);

export const Layout = () => {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [mobileOpen, setMobileOpen] = useState(false);
  const me = useQuery({
    queryKey: ['admin-me'],
    queryFn: () => api.request<{ email: string }>('/v1/admin/me'),
  });
  const email = me.data?.email ?? '—';

  return (
    <div className="flex min-h-full">
      <aside className="hidden w-60 shrink-0 flex-col border-r border-border bg-surface md:flex">
        <div className="flex h-14 items-center gap-2 border-b border-border px-4">
          <img
            src="/logo/placa.svg"
            alt="Estacione"
            width={28}
            height={28}
            className="rounded-sm"
          />
          <span className="font-semibold tracking-tight">Backoffice</span>
        </div>
        <nav className="flex-1 overflow-y-auto p-2" aria-label="Navegação">
          <NavLinks pathname={pathname} />
        </nav>
        <div className="border-t border-border p-4 text-xs text-muted-fg">
          <p className="mb-1 uppercase tracking-widest">Logado como</p>
          <p className="truncate font-medium text-fg" title={email}>
            {email}
          </p>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 border-b border-border bg-surface/95 pt-[env(safe-area-inset-top)] backdrop-blur-md">
          <div className="flex h-14 items-center gap-2 px-3 md:px-6">
            <Dialog open={mobileOpen} onOpenChange={setMobileOpen}>
              <DialogTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden" aria-label="Abrir menu">
                  <Menu className="h-5 w-5" />
                </Button>
              </DialogTrigger>
              <DialogContent className="left-0 top-0 flex h-full w-[85vw] max-w-xs translate-x-0 translate-y-0 flex-col rounded-none border-r border-border p-0 pt-[env(safe-area-inset-top)] sm:rounded-none [&>button]:right-3 [&>button]:top-[max(0.75rem,env(safe-area-inset-top))]">
                <div className="flex h-14 items-center gap-2 border-b border-border px-4">
                  <img
                    src="/logo/placa.svg"
                    alt="Estacione"
                    width={24}
                    height={24}
                    className="rounded-sm"
                  />
                  <span className="font-semibold tracking-tight">Backoffice</span>
                </div>
                <nav className="flex-1 overflow-y-auto p-2" aria-label="Navegação">
                  <NavLinks pathname={pathname} onNavigate={() => setMobileOpen(false)} />
                </nav>
                <div className="border-t border-border p-4 pb-[max(1rem,env(safe-area-inset-bottom))] text-xs text-muted-fg">
                  <p className="mb-1 uppercase tracking-widest">Logado como</p>
                  <p className="truncate font-medium text-fg" title={email}>
                    {email}
                  </p>
                </div>
              </DialogContent>
            </Dialog>

            <div className="flex min-w-0 items-center gap-2 md:hidden">
              <img src="/logo/placa.svg" alt="" width={24} height={24} className="rounded-sm" />
              <span className="truncate text-sm font-semibold tracking-tight">Backoffice</span>
            </div>

            <div className="hidden min-w-0 flex-1 md:block">
              <GlobalSearch />
            </div>
            <p
              className="hidden max-w-[14rem] truncate text-xs text-muted-fg lg:block"
              title={email}
            >
              {email}
            </p>
          </div>
          <div className="border-t border-border px-3 py-2 md:hidden">
            <GlobalSearch />
          </div>
        </header>

        <main className="min-w-0 flex-1 bg-bg px-3 py-4 sm:px-6 sm:py-6 md:px-8">
          <div className="mx-auto w-full max-w-6xl space-y-6">
            {/* Sem isso, uma API que recusa o backoffice parece um negócio zerado. */}
            {me.error ? (
              <div
                className="rounded border border-danger/40 bg-danger/10 p-3 text-sm text-danger"
                role="alert"
              >
                A API recusou o acesso do backoffice: {me.error.message} Os dados desta tela não
                carregaram. Recarregue a página; se continuar, confira o Cloudflare Access.
              </div>
            ) : null}
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

const NavLinks = ({ pathname, onNavigate }: { pathname: string; onNavigate?: () => void }) => (
  <>
    {NAV.map(({ to, label, icon: Icon }) => {
      const active = isActivePath(pathname, to);
      return (
        <Link
          key={to}
          to={to}
          onClick={onNavigate}
          className={cn(
            'flex items-center gap-2 rounded px-3 py-2.5 text-sm transition-colors md:py-2',
            active
              ? 'bg-muted font-medium text-fg'
              : 'text-muted-fg hover:bg-muted/60 hover:text-fg',
          )}
        >
          <Icon className="h-4 w-4 shrink-0" />
          {label}
        </Link>
      );
    })}
  </>
);
